import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Modal,
  Form,
  InputNumber,
  Input,
  Select,
  DatePicker,
  Checkbox,
} from 'antd';

import { useToasts } from 'core/hooks';
import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useCreatePlannedPaymentMutation,
  useUpdatePlannedPaymentMutation,
  PlannedPaymentInput,
  UpdatePlannedPaymentInput,
} from 'core';

const CURRENCIES = ['PLN', 'EUR', 'USD', 'BYN', 'RUB'];
const STATUSES = ['planned', 'confirmed', 'paid', 'cancelled'];
const DATE_FMT = 'YYYY-MM-DD';

/** Minimal shape of a calendar row this modal can edit/override. */
export interface IEditableRow {
  direction: string;
  source: string;
  kind: string;
  title?: string | null;
  date?: string | null;
  amount?: number | null;
  currency?: string | null;
  comment?: string | null;
  status?: string | null;
  plannedPaymentId?: number | null;
  sourceRef?: string | null;
}

interface IProps {
  open: boolean;
  /** When set → edit/override that row; when null → create a new manual row. */
  row: IEditableRow | null;
  /**
   * Direction forced for the CREATE flow. Expenses now live in `Expense` (the
   * ExpenseFormPopup), so the calendar opens this modal income-only — the
   * direction selector is hidden and `direction` is fixed to this value.
   */
  createDirection?: 'income' | 'expense';
  onClose: () => void;
  onSaved: () => void;
}

interface IFormValues {
  direction: string;
  recurring: boolean;
  date: moment.Moment;
  amount: number;
  currency: string;
  status: string;
  comment?: string;
  recurrenceDay?: number;
}

const STORED_SOURCES = new Set(['manual', 'recurring']);

/** Parse a derived row's sourceRef into the override link fields. */
const overrideLinkFromSourceRef = (
  sourceRef: string | null | undefined,
): Partial<PlannedPaymentInput> & { kind: string } => {
  const base = (sourceRef || '').split('#')[0];
  const [kind, ...rest] = base.split(':');
  if (kind === 'contribution') {
    return { kind: 'income_override', relatedContributionId: rest[0] };
  }
  if (kind === 'invoice') {
    return { kind: 'income_override', relatedFakturowniaInvoiceId: rest[0] };
  }
  if (kind === 'teacher_payout') {
    const [appId, period] = rest; // APPID, 'YYYY-MM'
    const [year, month] = (period || '').split('-');
    return {
      kind: 'teacher_payout_override',
      relatedApplicationId: appId,
      periodYear: Number(year) || undefined,
      periodMonth: Number(month) || undefined,
    };
  }
  return { kind: 'income_override' };
};

export const PlannedPaymentModal = memo<IProps>(
  ({ open, row, createDirection, onClose, onSaved }) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const [form] = Form.useForm<IFormValues>();

    const [
      createMutation,
      { loading: creating },
    ] = useCreatePlannedPaymentMutation();
    const [
      updateMutation,
      { loading: updating },
    ] = useUpdatePlannedPaymentMutation();

    const isStored = !!row && STORED_SOURCES.has(row.source);
    const isOverride = !!row && !isStored; // editing a derived row
    const isCreate = !row;

    const lockedDirection = !row ? createDirection : undefined;

    const initial = useMemo<Partial<IFormValues>>(
      () => ({
        direction: row?.direction || lockedDirection || 'expense',
        recurring: row?.kind === 'recurring',
        date: row?.date ? moment(row.date) : moment(),
        amount: row?.amount ?? undefined,
        currency: row?.currency || 'PLN',
        status: row?.status || 'planned',
        comment: row?.comment || '',
      }),
      [row, lockedDirection],
    );

    useEffect(() => {
      if (open) form.setFieldsValue(initial);
    }, [open, initial, form]);

    const notify = useCallback(
      (type: ToastTypes, title: string, text?: string) =>
        addToast({
          data: { type, title, text },
          type: GlobalToastTypeNames.PaymentCalendar,
        }),
      [addToast],
    );

    const onSubmit = useCallback(async () => {
      const values = await form.validateFields();
      const dateStr = values.date.format(DATE_FMT);
      try {
        if (isStored && row?.plannedPaymentId) {
          const input: UpdatePlannedPaymentInput = {
            id: String(row.plannedPaymentId),
            date: dateStr,
            amount: values.amount,
            currency: values.currency,
            comment: values.comment,
            status: values.status,
          };
          const res = await updateMutation({ variables: { input } });
          const payload = res.data?.updatePlannedPayment;
          if (!payload?.success) throw new Error(payload?.errors?.join('\n'));
        } else {
          const link = isOverride
            ? overrideLinkFromSourceRef(row?.sourceRef)
            : { kind: values.recurring ? 'recurring' : 'manual' };
          const input: PlannedPaymentInput = {
            direction: row?.direction || values.direction,
            date: dateStr,
            amount: values.amount,
            currency: values.currency,
            comment: values.comment,
            status: values.status,
            recurrence: values.recurring ? 'monthly' : 'none',
            recurrenceDay: values.recurring
              ? values.recurrenceDay || values.date.date()
              : undefined,
            ...link,
          };
          const res = await createMutation({ variables: { input } });
          const payload = res.data?.createPlannedPayment;
          if (!payload?.success) throw new Error(payload?.errors?.join('\n'));
        }
        notify(ToastTypes.success, t('pages.paymentCalendar.toasts.saved'));
        onSaved();
        onClose();
      } catch (error) {
        notify(
          ToastTypes.error,
          t('pages.paymentCalendar.toasts.saveFailed'),
          (error as Error).message,
        );
      }
    }, [
      form,
      isStored,
      isOverride,
      row,
      updateMutation,
      createMutation,
      notify,
      t,
      onSaved,
      onClose,
    ]);

    const title = isCreate
      ? t('pages.paymentCalendar.modal.createTitle')
      : isOverride
      ? t('pages.paymentCalendar.modal.overrideTitle')
      : t('pages.paymentCalendar.modal.editTitle');

    return (
      <Modal
        visible={open}
        title={title}
        onOk={onSubmit}
        onCancel={onClose}
        confirmLoading={creating || updating}
        okText={t('pages.paymentCalendar.modal.save')}
        cancelText={t('pages.paymentCalendar.modal.cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={initial}>
          {isCreate && !lockedDirection && (
            <Form.Item
              name="direction"
              label={t('pages.paymentCalendar.modal.direction')}
            >
              <Select>
                <Select.Option value="expense">
                  {t('pages.paymentCalendar.expense')}
                </Select.Option>
                <Select.Option value="income">
                  {t('pages.paymentCalendar.income')}
                </Select.Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="date"
            label={t('pages.paymentCalendar.modal.date')}
            rules={[{ required: true }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="amount"
            label={t('pages.paymentCalendar.modal.amount')}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="currency"
            label={t('pages.paymentCalendar.modal.currency')}
          >
            <Select>
              {CURRENCIES.map((c) => (
                <Select.Option key={c} value={c}>
                  {c}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label={t('pages.paymentCalendar.modal.status')}
          >
            <Select>
              {STATUSES.map((s) => (
                <Select.Option key={s} value={s}>
                  {t(`pages.paymentCalendar.statuses.${s}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="comment"
            label={t('pages.paymentCalendar.modal.comment')}
          >
            <Input.TextArea rows={2} maxLength={1000} />
          </Form.Item>

          {isCreate && (
            <>
              <Form.Item name="recurring" valuePropName="checked">
                <Checkbox>
                  {t('pages.paymentCalendar.modal.recurring')}
                </Checkbox>
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) => prev.recurring !== cur.recurring}
              >
                {({ getFieldValue }) =>
                  getFieldValue('recurring') ? (
                    <Form.Item
                      name="recurrenceDay"
                      label={t('pages.paymentCalendar.modal.recurrenceDay')}
                    >
                      <InputNumber min={1} max={31} style={{ width: '100%' }} />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    );
  },
);
